import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Phone, Mail, Globe, Store, Utensils, Briefcase, Building, Star } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  restaurant: Utensils,
  store: Store,
  service: Briefcase,
  gurudwara: Building,
  other: Store
};

const LocalDirectoryPage = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = ['All', 'Restaurant', 'Store', 'Service', 'Gurudwara', 'Other'];

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(`${API_URL}/advertisements`, {
          params: { ad_type: 'business' }
        });
        setAds(response.data);
      } catch (error) {
        console.error('Error fetching ads:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  const filteredAds = ads.filter(ad => {
    const matchesSearch = 
      ad.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === 'All' || 
      ad.category?.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const featuredAds = filteredAds.filter(ad => ad.is_featured);
  const regularAds = filteredAds.filter(ad => !ad.is_featured);

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="local-directory-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Local Sikh Directory
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover Sikh-owned businesses, restaurants, services, and Gurudwaras in your community.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search businesses, services..."
              className="pl-12 py-6 rounded-full border-saffron-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="directory-search"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category || (category === 'All' && !selectedCategory) ? 'default' : 'outline'}
                className={`rounded-full ${
                  selectedCategory === category || (category === 'All' && !selectedCategory) 
                    ? 'bg-saffron-500 hover:bg-saffron-600' 
                    : 'border-saffron-200 text-saffron-700 hover:bg-saffron-50'
                }`}
                onClick={() => setSelectedCategory(category === 'All' ? '' : category)}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Featured Listings */}
        {featuredAds.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-2xl font-bold text-royal-700 mb-6 flex items-center gap-2">
              <Star className="w-6 h-6 text-gold-500" />
              Featured Listings
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredAds.map((ad) => {
                const IconComponent = categoryIcons[ad.category?.toLowerCase()] || Store;
                return (
                  <Card 
                    key={ad.id} 
                    className="card-hover border-2 border-gold-300 bg-gradient-to-br from-white to-gold-50"
                    data-testid={`featured-ad-${ad.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        {ad.image_url ? (
                          <img 
                            src={ad.image_url} 
                            alt={ad.business_name} 
                            className="w-24 h-24 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-24 h-24 bg-saffron-100 rounded-lg flex items-center justify-center">
                            <IconComponent className="w-10 h-10 text-saffron-500" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge className="bg-gold-500 text-white mb-2">Featured</Badge>
                              <h3 className="font-heading text-xl font-semibold text-gray-900">
                                {ad.business_name || ad.title}
                              </h3>
                            </div>
                          </div>
                          <p className="text-gray-600 text-sm mt-1 line-clamp-2">{ad.description}</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gold-200 grid grid-cols-2 gap-3 text-sm">
                        {ad.address && (
                          <span className="flex items-center gap-2 text-gray-600">
                            <MapPin className="w-4 h-4 text-saffron-500" />
                            {ad.address}
                          </span>
                        )}
                        {ad.contact_phone && (
                          <a href={`tel:${ad.contact_phone}`} className="flex items-center gap-2 text-royal-600 hover:text-royal-700">
                            <Phone className="w-4 h-4" />
                            {ad.contact_phone}
                          </a>
                        )}
                        {ad.contact_email && (
                          <a href={`mailto:${ad.contact_email}`} className="flex items-center gap-2 text-royal-600 hover:text-royal-700">
                            <Mail className="w-4 h-4" />
                            Email
                          </a>
                        )}
                        {ad.website && (
                          <a href={ad.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-royal-600 hover:text-royal-700">
                            <Globe className="w-4 h-4" />
                            Website
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Regular Listings */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4"></div>
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : regularAds.length === 0 && featuredAds.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600">No listings found</h3>
              <p className="text-gray-500 mb-6">Be the first to add your business!</p>
              <Button onClick={() => window.location.href = '/advertise'} className="btn-primary">
                Add Your Business
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularAds.map((ad) => {
              const IconComponent = categoryIcons[ad.category?.toLowerCase()] || Store;
              return (
                <Card 
                  key={ad.id} 
                  className="card-hover bg-white border-gray-100"
                  data-testid={`ad-${ad.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      {ad.image_url ? (
                        <img 
                          src={ad.image_url} 
                          alt={ad.business_name} 
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-saffron-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-8 h-8 text-saffron-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-1 text-xs capitalize">
                          {ad.category || 'Business'}
                        </Badge>
                        <h3 className="font-semibold text-gray-900 truncate">
                          {ad.business_name || ad.title}
                        </h3>
                        <p className="text-gray-500 text-sm line-clamp-2 mt-1">{ad.description}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-3 border-t border-gray-100 space-y-2 text-sm">
                      {ad.address && (
                        <p className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 text-saffron-500 flex-shrink-0" />
                          <span className="truncate">{ad.address}</span>
                        </p>
                      )}
                      <div className="flex gap-3">
                        {ad.contact_phone && (
                          <a href={`tel:${ad.contact_phone}`} className="flex items-center gap-1 text-royal-600 hover:text-royal-700">
                            <Phone className="w-4 h-4" />
                            Call
                          </a>
                        )}
                        {ad.website && (
                          <a href={ad.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-royal-600 hover:text-royal-700">
                            <Globe className="w-4 h-4" />
                            Visit
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-royal-600 to-royal-800 rounded-2xl p-8 text-white">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Want to List Your Business?
          </h2>
          <p className="text-royal-100 mb-6 max-w-xl mx-auto">
            Join the Khalsa Sidak directory and connect with the global Sikh community.
          </p>
          <Button 
            onClick={() => window.location.href = '/advertise'} 
            className="bg-saffron-500 hover:bg-saffron-600 text-white rounded-full px-8 py-3"
          >
            Submit Your Listing
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocalDirectoryPage;
